import {
    Button,
    Card,
    Empty,
    Flex,
    Mentions,
    Modal,
    ModalProps,
    Popconfirm,
    Skeleton,
    Space,
    Spin,
    Tag,
    Typography,
} from 'antd'
import { CandidateAvatar } from './candidate-avatar'
import { CandidateAttributes } from './candidate-attributes'
import { MessageOutlined } from '@ant-design/icons'
import { UserAvatar } from '../../user/components/user-avatar'
import { Chat } from '../../components/chat/chat'
import React, { useEffect, useState } from 'react'
import { useCandidate, useLiveConversation, useRejectCandidate } from '../hooks'
import { useAuth } from '../../auth'
import { useTranslation } from 'react-i18next'
import { useCompanyUsers } from '../../company/hooks'
import { useStoreComment } from '../../comment/hooks'
import { Comment } from '../../comment/components/comment'
import { isRejectable } from '../candidate'
import { useMessageTemplates } from '../../campaign/message-template-hooks'
import { TemplateSelect } from '../../campaign/components/create/steps/template-select'
import { REJECT_CANDIDATE, SHOW_COMMENT } from '../../user/permission'

export type CandidateModalProps = { candidateId?: string } & ModalProps
export function CandidateModal({ candidateId, ...rest }: CandidateModalProps) {
    const candidate = useCandidate(candidateId)
    useLiveConversation(candidateId)
    const { user, can } = useAuth()
    const { t } = useTranslation()
    const [comment, setComment] = useState('')

    const companyUsers = useCompanyUsers(user?.activeCompany?.id)
    const storeComment = useStoreComment(candidateId)
    const rejectCandidate = useRejectCandidate(candidateId)
    const [rejectTemplateId, setRejectTemplateId] = useState<string>()
    const rejectTemplatesQuery = useMessageTemplates({
        enabled: !!candidate.data?.campaign?.whatsappBusinessAccountId,
        preservePreviousData: false,
        query: {
            size: 100,
            sort: '-created_at',
            filters: {
                purpose: ['reject'],
                status: ['APPROVED'],
                whatsappBusinessAccountId: candidate.data?.campaign?.whatsappBusinessAccountId
                    ? [candidate.data.campaign.whatsappBusinessAccountId]
                    : undefined,
            },
        },
    })

    useEffect(() => {
        setRejectTemplateId(undefined)
    }, [candidate.data?.id, candidate.data?.campaign?.whatsappBusinessAccountId])

    const handleStoreComment = () => {
        storeComment
            .mutateAsync({
                content: comment,
            })
            .then(() => {
                setComment('')
            })
    }

    return (
        <Modal
            footer={false}
            width={970}
            title={
                candidate.isPending ? (
                    <Skeleton.Input
                        className="w-64 h-7"
                        active
                    />
                ) : (
                    <Space>
                        {candidate.data && (
                            <CandidateAvatar
                                size={30}
                                candidate={candidate.data}
                            />
                        )}
                        <Flex vertical>
                            <Space>
                                {candidate.data?.name}
                                <a href={`tel:${candidate.data?.phone}`}>
                                    <Typography.Text type="secondary">{candidate.data?.phone}</Typography.Text>
                                </a>
                                <a href={`mailto:${candidate.data?.email}`}>
                                    <Typography.Text type="secondary">{candidate.data?.email}</Typography.Text>
                                </a>
                                {candidate.data && candidate.data.compatibility !== null && (
                                    <Typography.Text className="text-xs">
                                        {candidate.data.compatibility >= 70 && <>🏆</>} {candidate.data.compatibility}%
                                    </Typography.Text>
                                )}
                                {candidate.data?.archivedAt !== null && <Tag>{t('Archived')}</Tag>}
                            </Space>
                            <Typography.Text type="secondary"> {candidate.data?.campaign?.title}</Typography.Text>
                        </Flex>
                    </Space>
                )
            }
            {...rest}
        >
            <Flex
                justify="space-between"
                className="mt-4 flex-col lg:flex-row"
                gap="middle"
                align="start"
            >
                <Flex
                    vertical
                    gap="middle"
                    className="ml-1.5 w-full"
                >
                    {candidate.data?.status === 'finished' ? (
                        <div className="bg-surface-muted p-4 rounded-xl">
                            {candidate.data?.positive.length > 0 || candidate.data?.negative.length > 0 ? (
                                <CandidateAttributes
                                    positive={candidate.data?.positive}
                                    negative={candidate.data?.negative}
                                />
                            ) : (
                                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                            )}
                        </div>
                    ) : (
                        <div className="bg-surface-muted p-4 rounded-xl">
                            {candidate.isPending ? (
                                <Skeleton
                                    active
                                    title={false}
                                    paragraph={{ rows: 4, width: '100%' }}
                                />
                            ) : (
                                <Typography.Text
                                    type="secondary"
                                    className="py-2"
                                >
                                    {t('Candidate attributes will appear here once the conversation is complete')}
                                </Typography.Text>
                            )}
                        </div>
                    )}

                    {can(SHOW_COMMENT) && (
                        <Flex
                            vertical
                            gap="middle"
                            align="start"
                        >
                            <Flex
                                align="center"
                                gap="middle"
                            >
                                <MessageOutlined className="ml-1 text-base" />
                                <Typography.Title
                                    level={4}
                                    className="mb-0"
                                >
                                    {t('Comments')}
                                </Typography.Title>
                            </Flex>
                            <Flex
                                vertical
                                gap="small"
                                className="w-full"
                                align="start"
                            >
                                <Flex
                                    align="start"
                                    gap="small"
                                    className="-ml-0.5 w-full"
                                >
                                    <UserAvatar user={user!} />
                                    <Mentions
                                        autoSize={{
                                            minRows: 2,
                                        }}
                                        disabled={candidate.data?.archivedAt !== null}
                                        className="w-full"
                                        placeholder={t(
                                            "Leave a comment for your team. Use @ to mention someone, they'll get notified."
                                        )}
                                        onChange={(value) => {
                                            setComment(value)
                                        }}
                                        value={comment}
                                        onKeyDown={(event) => {
                                            if (
                                                event.key === 'Enter' &&
                                                comment.length > 0 &&
                                                (event.metaKey || event.ctrlKey)
                                            ) {
                                                event.preventDefault()
                                                event.stopPropagation()
                                                handleStoreComment()
                                            }
                                        }}
                                        loading={companyUsers.isPending}
                                        options={
                                            companyUsers.data?.map((user) => ({
                                                value: user.name,
                                                label: (
                                                    <Space size="small">
                                                        <UserAvatar
                                                            size="small"
                                                            user={{ avatar: user.avatar! }}
                                                        />
                                                        {user.name}
                                                    </Space>
                                                ),
                                            })) || []
                                        }
                                    />
                                </Flex>
                                {comment.length > 0 && (
                                    <Button
                                        type="primary"
                                        className="w-auto ml-9"
                                        loading={storeComment.isPending}
                                        onClick={handleStoreComment}
                                    >
                                        {t('Add')}
                                    </Button>
                                )}
                            </Flex>

                            <Flex
                                vertical
                                gap="middle"
                                className="w-full"
                                style={{
                                    maxHeight: '464px',
                                    overflowY: 'auto',
                                }}
                            >
                                {candidate.data?.comments?.map((comment) => (
                                    <Comment
                                        key={comment.id}
                                        comment={comment}
                                    />
                                ))}
                            </Flex>
                        </Flex>
                    )}
                </Flex>

                <Spin spinning={candidate.isPending}>
                    <Card
                        size="small"
                        className="lg:min-w-[450px] lg:w-[450px]"
                    >
                        <Chat
                            height={700}
                            messages={candidate.data?.messages || []}
                        />
                        {can(REJECT_CANDIDATE) && candidate.data && isRejectable(candidate.data) && (
                            <Popconfirm
                                styles={{
                                    body: {
                                        maxWidth: '400px',
                                    },
                                }}
                                title={t('Send rejection message to candidate?')}
                                description={
                                    <Flex
                                        vertical
                                        gap="small"
                                        className="w-[min(360px,calc(100vw-64px))]"
                                    >
                                        <TemplateSelect
                                            templates={rejectTemplatesQuery.data?.data || []}
                                            loading={rejectTemplatesQuery.isLoading}
                                            value={rejectTemplateId}
                                            onChange={setRejectTemplateId}
                                            placeholder={t('Select template')}
                                        />
                                    </Flex>
                                }
                                okText={t('Send message')}
                                cancelText={t('No')}
                                okButtonProps={{
                                    disabled: !rejectTemplateId,
                                }}
                                onConfirm={() =>
                                    rejectTemplateId
                                        ? rejectCandidate.mutateAsync({ messageTemplateId: rejectTemplateId })
                                        : undefined
                                }
                            >
                                <Button
                                    block
                                    className="mt-2"
                                    danger
                                >
                                    {t('Reject')}
                                </Button>
                            </Popconfirm>
                        )}
                    </Card>
                </Spin>
            </Flex>
        </Modal>
    )
}
